/**
 * What staff type on a contract, in the contracts store (PostgreSQL on the engine's server; owner, 27 Sep 2026:
 * "we will not rely on Firebase anymore"): the Monthly Calculator's months and header fields, the notes and the
 * History - read and written through the engine's /api/store/contracts/{no}/staff routes (engine/api/staff_routes.py,
 * engine/api/contract_staff.py). The Monthly page uses it only when REACT_APP_CONTRACTS_V2 is on (USE_CONTRACTS_V2);
 * with it off every writer saves to Firestore as before.
 *
 * The calculator must open a contract the way the server checks its saves (contract_staff.py's module note), or a
 * save of what it does by itself is refused for a user without edit_monthly_leasing:
 *   - its months = staff.monthly_calculations of GET .../staff, and its Save names that `version`;
 *   - its monthly rate / rate schedule = effective.total_rent / effective.rate_schedule of the same GET;
 *   - its pick-up / drop-off = their DUBAI calendar day (contractsV2.dubaiText), never the UTC date of an ISO time;
 *   - its status = the store's status text; its today = Dubai's today (contractRules.dubaiToday, as today).
 * A save sends only staff fields (snake_case), every month's money / KM cell a JSON number or plain ASCII digits, no
 * `comments` (a note is saved on its own) and no net balance (the server adds it up from the months).
 * A save made on an older version answers 409 {code: 'contract-changed', by, at, source, version, detail}: nothing
 * was written; a 403 names the permission that is missing.
 */
import { finesService } from './finesService';
import { CONTRACT_CHANGED } from './contractVersion';
import { dubaiText } from './contractsV2';

/** Where a save comes from (staff_routes.WEB_SOURCES): the calculator, the page's Edit Mode, its month sync. */
export const STAFF_SOURCE = { calculator: 'Calc', table: 'Table', autoSync: 'Auto-Sync' };

/** Error codes (besides CONTRACT_CHANGED, the 409) a store call throws with. */
export const STAFF_FORBIDDEN = 'staff-forbidden';       // 403: the detail names the missing permission
export const STAFF_BAD_INPUT = 'staff-bad-input';       // 400 (or refused here before sending)
export const STAFF_NOT_FOUND = 'staff-not-found';       // 404
export const STAFF_UNAVAILABLE = 'staff-unavailable';   // 503 / no answer

const staffError = (code, message, extra = {}) => Object.assign(new Error(message), { code, staffError: true, ...extra });

/** A failed store call as an Error the page can show: `code` (above), `message` (the engine's own sentence). */
export const staffErrorOf = (err) => {
    if (err && err.staffError) return err;
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    const detail = typeof data.detail === 'string' ? data.detail : '';
    if (status === 409 && data.code === CONTRACT_CHANGED) {
        return staffError(CONTRACT_CHANGED, detail || 'This contract was changed by someone else after you opened it. Nothing was saved - open it again to see the latest months, then redo your change.', {
            status, conflict: { by: data.by || '', at: data.at || '', source: data.source || '', version: data.version },
        });
    }
    if (status === 403) return staffError(STAFF_FORBIDDEN, detail || 'Your yelo profile lacks the permission for this change.', { status });
    if (status === 400) return staffError(STAFF_BAD_INPUT, detail ? `Not saved: ${detail}` : 'Not saved: the store refused a value.', { status });
    if (status === 404) return staffError(STAFF_NOT_FOUND, detail || 'This contract is not in the contracts store.', { status });
    if (!err?.response) {
        // no answer (a timeout, the network): a save may still have gone through - its next save would say so (409)
        return staffError(STAFF_UNAVAILABLE, 'The contracts store did not answer - open the contract again to see whether the change was saved.', { status });
    }
    if (status === 503) return staffError(STAFF_UNAVAILABLE, 'The contracts store is unavailable - nothing was saved. Try again in a minute.', { status });
    return staffError(STAFF_UNAVAILABLE, detail || err?.message || 'The contracts store did not answer.', { status });
};

const call = async (fn) => {
    try {
        return await fn();
    } catch (err) {
        throw staffErrorOf(err);
    }
};

/** The contract number a page row carries (the store's key). */
export const contractNoOf = (row) => String(row?.contractNo || row?.['Contract No.'] || row?.contract_no || row?.id || '').trim().toUpperCase();

// ---- reading ------------------------------------------------------------------------------------------------------

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * A pick-up / drop-off as the calculator must read it: an ISO instant (the store's Ejar time) becomes its Dubai wall
 * time "dd/mm/yyyy HH:MM", whose date part is the Dubai calendar day - the UTC date part of the ISO text is a day
 * earlier for a time between 00:00 and 04:00 Dubai (a day's rent less: review 3). A time without a zone is UTC (the
 * engine's own rule). Any other value (the page rows' Dubai text already) is kept as it is.
 */
export const dubaiDayText = (value) => {
    if (typeof value !== 'string') return value;
    const s = value.trim();
    if (!ISO_INSTANT.test(s)) return value;
    // milliseconds at most (not every browser reads 6 fraction digits), 'T' between the date and the time
    const iso = s.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
    const text = dubaiText(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
    return text || value;
};

// a store time with milliseconds at most: the pages read it with `new Date(...)`, and not every browser reads 6 digits
const msTime = (v) => (typeof v === 'string' ? v.replace(/(\.\d{3})\d+/, '$1') : v || '');

/** The store's notes as the calculator's comments ({id, text, user, timestamp, editedAt}). */
export const notesAsComments = (notes) => (Array.isArray(notes) ? notes : []).map((n) => ({
    id: String(n.id),
    text: n.text || '',
    user: n.user || '',
    timestamp: msTime(n.at || n.timestamp),
    ...(n.edited_at || n.editedAt ? { editedAt: msTime(n.edited_at || n.editedAt) } : {}),
}));

/** The store's History (newest first, as Firestore's update_history) as the calculator's History entries. */
export const historyAsUpdates = (history) => (Array.isArray(history) ? history : []).map((h) => ({
    timestamp: msTime(h.at || h.timestamp),
    user: h.user || '',
    source: h.source || '',
    changes: Array.isArray(h.changes) ? h.changes : [],
}));

const DATE_KEYS = ['pickup_date', 'Pick-up Date', 'pickupDate', 'dropoff_date', 'Drop-off Date', 'dropoffDate'];
// the contract's rent under other names: the calculator and the sync read `total_rent || 'Total Rent' || rent ...`,
// so only the store's effective rent may be left (a stale one from an earlier save in the page would win)
const OTHER_RENT_KEYS = ['Total Rent', 'Rent', 'rent', 'dealer_monthly_fee'];

/**
 * A page row with its staff data from the store (GET .../staff), as the calculator opens it and the month sync
 * reads it: the months, the header fields, the notes, the History and the version the Save must name; the monthly
 * rate / rate schedule the store's EFFECTIVE ones (staff's override over Ejar's); the pick-up / drop-off their Dubai
 * day. A hand-typed Invygo car (staff) is shown over the one Ejar's first car gave.
 */
export const withStoredStaff = (row, view) => {
    const staff = view?.staff || {};
    const effective = view?.effective || {};
    const out = { ...row };
    DATE_KEYS.forEach((k) => { if (k in out) out[k] = dubaiDayText(out[k]); });
    OTHER_RENT_KEYS.forEach((k) => { delete out[k]; });
    const schedule = Array.isArray(effective.rate_schedule) ? effective.rate_schedule : [];
    Object.assign(out, {
        monthly_calculations: Array.isArray(staff.monthly_calculations) ? staff.monthly_calculations : [],
        total_rent: effective.total_rent ?? '',
        rate_schedule: schedule,
        rent_source: effective.rent_source || out.rent_source || '',
        // a returned contract's Trip Days as Ejar counted them after the return (null: not known) - its last month
        // is billed up to the pick-up day + these days (owner, 14 Oct 2026)
        ejar_days: Number.isInteger(effective.ejar_days) && effective.ejar_days > 0 ? effective.ejar_days : null,
        // Ejar itself closed it (its own status - an E-Check close comes first): its close items are final
        ejar_closed: effective.ejar_closed === true,
        // no monthly rate at all and Ejar books it for under 30 days: Ejar's own rent for its FIRST month only
        // (owner, 14 Oct 2026) - never the contract's monthly rate (the Auto-Sync and the balance never read it)
        ejar_short_rent: Number(effective.ejar_short_rent) > 0 ? Number(effective.ejar_short_rent) : null,
        invoiceCompanyName: staff.invoice_company_name || '',
        invoiceCompanyAddress: staff.invoice_company_address || '',
        invoiceCompanyTRN: staff.invoice_company_trn || '',
        invoiceKMLimit: staff.invoice_km_limit || 2500,
        pickupKM: staff.pickup_km || 0,
        addons: { fullInsurance: false, additionalDriver: false, extraKM: 0, ...(staff.addons || {}) },
        comments: notesAsComments(view?.notes),
        updateHistory: historyAsUpdates(view?.history),
        _staffVersion: Number.isInteger(view?.version) ? view.version : null,
        _staffSchedule: schedule,
    });
    if (staff.invygo_plate) {
        Object.assign(out, { invygo_plate: staff.invygo_plate, 'Invygo Plate': staff.invygo_plate, invygoPlate: staff.invygo_plate });
        Object.assign(out, { invygo_model: staff.invygo_model || '', 'Invygo Model': staff.invygo_model || '', invygoModel: staff.invygo_model || '' });
    }
    return out;
};

// ---- what a save sends ----------------------------------------------------------------------------------------------

// a month's money / KM cells (contract_staff.ROW_NUMBERS)
const ROW_NUMBERS = ['rent', 'vat', 'totalRent', 'salik', 'fine', 'parking', 'other', 'paid', 'Paid', 'totalAmount',
    'Balance', 'startKM', 'currentKM', 'salik_count'];
const PLAIN = /^-?\d+(?:\.\d+)?$/;                          // contract_staff.CELL_TEXT
const JS_SPACE = '[\\s\\uFEFF\\xA0]';                        // what String.prototype.trim / parseFloat skip
const LOOSE = new RegExp(`^${JS_SPACE}*([+-]?)(\\d*)(?:\\.(\\d*))?${JS_SPACE}*$`);

/**
 * One month cell as the store takes it: a finite JSON number, or plain ASCII digits (an optional '-' and '.digits').
 * A text of digits the page reads as that same number ("  3150 ", "+3150", ".5", "3150.") is written the plain way -
 * the same amount and the same truthiness to every page ("0" stays a text: to the Unpaid tab it is a payment, 0 is
 * not). NaN is written as null (both read as 0 and as "nothing"). Anything the pages would read as two different
 * amounts ("3,150": 3 in the calculator, 3150 in the table), an infinity, a true / false: refused, naming the cell.
 */
export const monthCell = (value, field, start) => {
    if (value === undefined || value === null || value === '') return value;
    const where = `${field} (${start || '?'})`;
    if (typeof value === 'number') {
        if (Number.isNaN(value)) return null;
        if (!Number.isFinite(value)) throw staffError(STAFF_BAD_INPUT, `Not saved: ${where} is not a number - type it again.`);
        return value;
    }
    if (typeof value === 'string') {
        if (PLAIN.test(value)) return value;
        const m = LOOSE.exec(value);
        if (m && (m[2] || m[3])) return `${m[1] === '-' ? '-' : ''}${m[2] || '0'}${m[3] ? `.${m[3]}` : ''}`;
    }
    throw staffError(STAFF_BAD_INPUT, `Not saved: ${where} "${String(value).slice(0, 30)}" is not a plain number - type it again.`);
};

/** The months with every money / KM cell in the form the store takes (new objects; the rows passed are not touched). */
export const cleanMonths = (rows) => (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row || typeof row !== 'object') return row;
    const out = { ...row };
    ROW_NUMBERS.forEach((f) => { if (f in out) out[f] = monthCell(out[f], f, row.start); });
    return out;
});

const DMY = /^\s*\d{1,2}[/-]\d{1,2}[/-]\d{4}\s*$/;
const amountOf = (v) => (typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, '')));
const sameEntry = (a, b) => String(a?.from || '').trim() === String(b?.from || '').trim()
    && Math.abs(amountOf(a?.rate) - amountOf(b?.rate)) < 0.005;
const isManual = (e) => String(e?.source || '').trim().toLowerCase() === 'manual';

/**
 * The rate schedule as the store keeps it: ONLY hand-typed entries (source 'manual') - the engine's own (from Ejar's
 * comments) stay with its record, and GET's effective schedule lays the hand-typed ones over them. So an entry the
 * calculator shows exactly as it opened from Ejar is not sent; every other entry - typed, edited (the header's
 * Monthly Rate changes the latest one), learned from the months - is a hand-typed one, as it was one more entry of
 * the contract's schedule in Firestore. An entry with no dd/mm/yyyy date or no rate above 0 cannot be kept (the
 * store refuses it and with it the whole save), so it is left out.
 */
export const manualSchedule = (schedule, openedSchedule = []) => {
    const opened = Array.isArray(openedSchedule) ? openedSchedule : [];
    return (Array.isArray(schedule) ? schedule : []).filter((e) => e && typeof e === 'object')
        .filter((e) => isManual(e) || !opened.some((o) => !isManual(o) && sameEntry(o, e)))
        .filter((e) => DMY.test(String(e.from || '')) && amountOf(e.rate) > 0)
        .map((e) => ({ from: String(e.from).trim(), rate: amountOf(e.rate), note: e.note ? String(e.note) : '', source: 'manual' }));
};

// the web's names -> the store's (contract_staff.ALIASES); the calculator's payload uses the web ones
const FIELD_OF = {
    monthly_calculations: 'monthly_calculations', monthlyCalculations: 'monthly_calculations',
    total_rent: 'total_rent', 'Total Rent': 'total_rent',
    rate_schedule: 'rate_schedule', rateSchedule: 'rate_schedule',
    invoice_company_name: 'invoice_company_name', invoiceCompanyName: 'invoice_company_name',
    invoice_company_address: 'invoice_company_address', invoiceCompanyAddress: 'invoice_company_address',
    invoice_company_trn: 'invoice_company_trn', invoiceCompanyTRN: 'invoice_company_trn',
    invoice_km_limit: 'invoice_km_limit', invoiceKMLimit: 'invoice_km_limit',
    pickup_km: 'pickup_km', pickupKM: 'pickup_km',
    addons: 'addons',
    invygo_plate: 'invygo_plate', invygoPlate: 'invygo_plate', 'Invygo Plate': 'invygo_plate',
    invygo_model: 'invygo_model', invygoModel: 'invygo_model', 'Invygo Model': 'invygo_model',
};
// never sent: a note is saved on its own; the balance is the server's sum of the months; the page's own flags
const NOT_SENT = new Set(['comments', 'net_balance', 'netBalance', 'Net Balance', '_syncUpdate', '_invygoPlateChanged']);

/** Edit Mode: the page columns that are staff data (the rest of a contract is Ejar's - changed in Ejar only). */
export const EDIT_MODE_FIELDS = { invygoPlate: 'invygo_plate', invygoModel: 'invygo_model' };

const numberOrNull = (v) => {
    if (v === '' || v === undefined || v === null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    return v;                                        // a text: the store reads it (thousands commas allowed) or says why
};

/**
 * A web save (the calculator's payload, an Edit Mode change) as the store's `changes`: staff fields only, under the
 * store's names; the months with clean cells; the rate schedule the hand-typed entries (manualSchedule, against the
 * schedule the calculator opened with); the add-ons their three keys. `comments`, the net balance and the page's flags
 * are never sent. Any other field is refused here - a save never carries Ejar's data.
 */
export const staffChangesOf = (payload, { openedSchedule = [] } = {}) => {
    const out = {};
    Object.entries(payload || {}).forEach(([key, value]) => {
        if (NOT_SENT.has(key) || value === undefined) return;
        const field = FIELD_OF[key];
        if (!field) throw staffError(STAFF_BAD_INPUT, `Not saved: ${key} is not staff data (Ejar is its source).`);
        if (field in out && key !== field) return;            // the store's own name wins over the web's
        if (field === 'monthly_calculations') out[field] = cleanMonths(value);
        else if (field === 'rate_schedule') out[field] = manualSchedule(value, openedSchedule);
        else if (field === 'addons') {
            const a = value || {};
            out[field] = { fullInsurance: !!a.fullInsurance, additionalDriver: !!a.additionalDriver, extraKM: numberOrNull(a.extraKM) ?? 0 };
        } else if (field === 'total_rent' || field === 'invoice_km_limit' || field === 'pickup_km') out[field] = numberOrNull(value);
        else out[field] = value ?? '';
    });
    return out;
};

// ---- the routes -----------------------------------------------------------------------------------------------------

/** GET .../staff: {staff, version, notes, history (last 50, newest first), effective, updated_by / at, source}. */
export const loadContractStaff = (contractNo) => call(() => finesService.storeStaff(contractNo));

/**
 * PUT .../staff with the `version` the contract was opened with. Returns the contract's staff view after the save
 * (its new `version`, the History with this save's lines). A 409 throws code CONTRACT_CHANGED with the engine's
 * sentence (who changed it, when, from where); a 403 the sentence naming the missing permission.
 */
export const saveContractStaff = (contractNo, changes, version, source = STAFF_SOURCE.calculator) => {
    if (!Number.isInteger(version) || version < 0) {
        return Promise.reject(staffError(STAFF_BAD_INPUT, 'Not saved: the version this contract was opened with is not known - close it and open it again.'));
    }
    if (!changes || !Object.keys(changes).length) return Promise.reject(staffError(STAFF_BAD_INPUT, 'Not saved: nothing to save.'));
    return call(() => finesService.storeStaffSave(contractNo, { changes, version, source }));
};

/**
 * Header fields saved from a page that did not open the months (Edit Mode): the version is read first, then the save
 * names it. The fields sent do not depend on the months, so a save that lost a race to another one is sent once more
 * on the newer version (Firestore's Edit Mode saves never checked a version); a second refusal is thrown.
 */
export const saveStaffFields = async (contractNo, changes, source = STAFF_SOURCE.table) => {
    for (let attempt = 0; ; attempt += 1) {
        const { version } = await loadContractStaff(contractNo);
        try {
            return await saveContractStaff(contractNo, changes, version, source);
        } catch (err) {
            if (err.code !== CONTRACT_CHANGED || attempt > 0) throw err;
        }
    }
};

/** A note (the calculator's comment) added / edited / deleted on its own: returns the note (the store's view). */
export const addContractNote = (contractNo, text) => call(() => finesService.storeStaffNoteAdd(contractNo, text));
export const editContractNote = (contractNo, noteId, text) => call(() => finesService.storeStaffNoteEdit(contractNo, noteId, text));
export const deleteContractNote = (contractNo, noteId) => call(() => finesService.storeStaffNoteDelete(contractNo, noteId));
